/* eslint-disable rulesdir/jsx-click-tracking-ids */
import { Color, Spacing } from '@persona-id/design-tokens';
import { EyeIcon, EyeOffIcon } from '@persona-id/design-tokens/icons';
import { repeat } from 'lodash';
import { FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import Pii from 'dashboard/components/Pii';
import { UppercaseLabel } from 'dashboard/components/Typography';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { Paragraph } from 'design-system/components/Text';
import { BASE } from 'design-system/variables/spacing';
import useToggle from 'lib/hooks/useToggle';

export const AttributeGrid = styled('div')<{
  colCount?: number;
}>`
  display: grid;
  grid-column-gap: ${Spacing[4]};
  grid-row-gap: ${Spacing[6]};
  grid-template-columns: ${({ colCount }) => `repeat(${colCount ?? 3}, 1fr)`};

  & + & {
    padding-bottom: ${Spacing[5]};
  }
`;

const InfoItem = styled('div')<{ width: string; colCount: number }>`
  min-width: 0;
  width: ${({ width }) => width};
  grid-column: span ${({ colCount }) => colCount};
`;

const SectionTitle = styled(UppercaseLabel)`
  color: ${Color.CoreSlate700};
`;

const InfoTitle = styled('div')<{ titleSpacing: number }>`
  color: ${Color.CoreSlate900};
  margin-bottom: ${({ titleSpacing }) => titleSpacing}px;
  display: flex;
  align-items: center;
`;

const ValueFormatter = styled('div')<{
  wordBreak?: WordBreak;
  textTransform?: TextTransform;
}>`
  color: ${Color.CoreSlate1000};
  word-break: ${({ wordBreak }) => wordBreak};

  ul,
  ol {
    list-style-position: inside;
  }

  ${({ textTransform }) =>
    textTransform === 'capitalize' &&
    css`
      text-transform: lowercase;

      &:first-line {
        text-transform: capitalize;
      }
    `}

  ${({ textTransform }) =>
    textTransform === 'sentence' &&
    css`
      text-transform: lowercase;

      &:first-letter,
      &:first-line {
        text-transform: capitalize;
      }
    `}
`;

const InfoPlaceholder = styled('div')`
  color: ${Color.CoreSlate700};
`;

const IconButtonContainer = styled('div')`
  display: flex;
  padding: 0 ${Spacing[2]};
  cursor: pointer;

  svg path {
    transition-duration: 150ms;
  }

  :hover {
    svg path {
      fill: ${Color.CorePurple900};
    }
  }
`;

export type WordBreak = 'break-all' | 'break-word' | 'normal';
export type TextTransform = 'capitalize' | 'sentence';

interface InfoValueProps {
  placeholder: ReactNode;
  show: boolean;
  value?: ReactNode;
  textTransform?: TextTransform;
  dataTest?: string;
  wordBreak?: WordBreak;
}

const InfoValue: FC<InfoValueProps> = ({
  placeholder,
  show,
  value,
  textTransform,
  dataTest,
  wordBreak,
}) => {
  if (value == null) {
    return <InfoPlaceholder data-test={dataTest}>{placeholder}</InfoPlaceholder>;
  }

  return (
    <ValueFormatter data-test={dataTest} wordBreak={wordBreak} textTransform={textTransform}>
      {show ? value : repeat('•', typeof value === 'string' ? value.length : 8)}
    </ValueFormatter>
  );
};

export interface AttributeItemProps {
  colCount?: number;
  width?: string;
  placeholder?: ReactNode;
  title?: ReactNode;
  sectionTitle?: ReactNode;
  toggleable?: boolean;
  wordBreak?: WordBreak;
  value?: ReactNode | ReactNode[];
  textTransform?: TextTransform;
  isRedacted?: boolean;
  helpMessage?: ReactNode;
  dataTest?: string;
  helpMessagePopoverWidth?: number;
  titleSpacing?: number;
}

const AttributeItem: FC<AttributeItemProps> = ({
  colCount = 1,
  width = 'fit-content',
  placeholder = '-',
  title,
  sectionTitle,
  toggleable = false,
  wordBreak = 'normal',
  textTransform,
  value,
  isRedacted,
  helpMessage,
  dataTest,
  helpMessagePopoverWidth = 300,
  titleSpacing = BASE,
  ...props
}) => {
  const [show, { toggle: toggleShow }] = useToggle(!toggleable);
  // Sometimes we get nulls because some verifications may have parsing errors, etc.
  // We also filter by non-null rather than Boolean here, because 0 is a perfectly valid display value.
  const valueAsArray = ([] as Array<ReactNode>)
    .concat(value instanceof Array ? value : [value])
    .filter((value) => value != null);
  const showToggleButton = toggleable && !isRedacted && valueAsArray.length > 0;

  return (
    <InfoItem data-test={dataTest} colCount={colCount} width={width} {...props}>
      {sectionTitle != null && <SectionTitle>{sectionTitle}</SectionTitle>}
      <InfoTitle titleSpacing={titleSpacing}>
        {title != null && (
          <Paragraph color='tertiary' data-test='attribute-item__label'>
            {title}
          </Paragraph>
        )}
        {showToggleButton && (
          <IconButtonContainer
            data-test='attribute-item__toggle'
            role='button'
            onClick={toggleShow}
          >
            {show ? <EyeIcon /> : <EyeOffIcon />}
          </IconButtonContainer>
        )}
        {helpMessage && <HelpIcon popoverWidth={helpMessagePopoverWidth}>{helpMessage}</HelpIcon>}
      </InfoTitle>
      <Pii isRedacted={!!isRedacted}>
        <div>
          {
            /**
             * If valueAsArray is empty it means all values were nullish.
             * Add a "fake" null to cause a placeholder to render.
             **/
            (valueAsArray.length > 0 ? valueAsArray : [null]).map((v, i) => (
              <InfoValue
                key={i}
                dataTest='attribute-item__value'
                value={v}
                placeholder={placeholder}
                show={show}
                wordBreak={wordBreak}
                textTransform={textTransform}
              />
            ))
          }
        </div>
      </Pii>
    </InfoItem>
  );
};

export default AttributeItem;
